/**
 * Chat unread email — admin API contracts (SCH-2312, SCH-2430 A4).
 *
 * A school admin reads and sets one person's chat email from that person's user
 * detail page. There is no school-wide default and no school-wide list. The
 * self-service read and write stay on the existing `/v1/notifications/preferences`
 * endpoints (type `chat_unread_email`).
 *
 *   GET /v1/notifications/chat-email/members/:userId   → that person's chat email state
 *   PUT /v1/notifications/chat-email/members/:userId   → turn it on or off for them
 *   GET/POST /v1/notifications/chat-email/unsubscribe/:token → public one-click unsubscribe (below)
 *
 * A person's own choice always wins: the admin write is refused with
 * `CHAT_EMAIL_USER_CHOICE` (409) when that person set it themselves, and the
 * stored value is left unchanged.
 */

import { z } from 'zod'

import { NotificationPreferenceSourceSchema } from '../../schemas/entities/notification-preference'

/** Error type returned (HTTP 409) when an admin tries to override a person's own choice. */
export const CHAT_EMAIL_USER_CHOICE_ERROR = 'CHAT_EMAIL_USER_CHOICE' as const

/** One person's chat email state, as a school admin sees it. */
export const ChatEmailMemberStateSchema = z.object({
  /**
   * Whether the person can have chat emails at all: an active member at the
   * school with chat on for them (the school's chat config). When false, the
   * admin control is hidden and a write is refused.
   */
  eligible: z.boolean(),
  /** The effective value: own row, else admin row, else off. */
  enabled: z.boolean(),
  source: NotificationPreferenceSourceSchema,
  /** False when the account has no address or only a placeholder one (AE8). */
  canReceiveEmail: z.boolean()
})
export type ChatEmailMemberState = z.infer<typeof ChatEmailMemberStateSchema>

export const ChatEmailMemberParamsSchema = z.object({
  /** UsersOnSchools public id at the active school. */
  userId: z.string().min(1, 'userId is required')
})
export type ChatEmailMemberParams = z.infer<typeof ChatEmailMemberParamsSchema>

export const ChatEmailMemberResponseSchema = z.object({
  data: ChatEmailMemberStateSchema,
  message: z.string()
})
export type ChatEmailMemberResponse = z.infer<typeof ChatEmailMemberResponseSchema>

// --- PUT /v1/notifications/chat-email/members/:userId ---

export const UpdateChatEmailMemberBodySchema = z.object({
  enabled: z.boolean()
})
export type UpdateChatEmailMemberBody = z.infer<typeof UpdateChatEmailMemberBodySchema>

// --- /v1/notifications/chat-email/unsubscribe/:token (public, no auth) ---
//
// The token is the whole credential and always travels in the URL path, so the
// same POST serves both callers (R13, KTD11):
//   - the web confirm page POSTs with no body (or any body; it is ignored)
//   - a mail client's RFC 8058 one-click POSTs `List-Unsubscribe=One-Click` as
//     `application/x-www-form-urlencoded` to the `List-Unsubscribe` header URL
// GET never changes anything (mail scanners prefetch links); it only reads the
// school name for the confirm page. Every token failure is one 404 NOT_FOUND.

export const ChatEmailUnsubscribeParamsSchema = z.object({
  /** `<keyId>.<schoolUserPublicId>.<signature>`, all base64url-safe characters. */
  token: z
    .string()
    .max(256)
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/)
})
export type ChatEmailUnsubscribeParams = z.infer<typeof ChatEmailUnsubscribeParamsSchema>

export const GetChatEmailUnsubscribeResponseSchema = z.object({
  data: z.object({ schoolName: z.string() }),
  message: z.string()
})
export type GetChatEmailUnsubscribeResponse = z.infer<typeof GetChatEmailUnsubscribeResponseSchema>

export const ChatEmailUnsubscribeResponseSchema = z.object({
  data: z.object({
    /** Always true on success, including when the preference was already off. */
    unsubscribed: z.literal(true),
    schoolName: z.string()
  }),
  message: z.string()
})
export type ChatEmailUnsubscribeResponse = z.infer<typeof ChatEmailUnsubscribeResponseSchema>
